import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Input, Button, Space } from 'antd';
import { SearchOutlined, CloseOutlined, LoadingOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';

/**
 * Reusable SearchInput component with debouncing and URL state management
 * 
 * @param {string} placeholder - Placeholder text for the search input
 * @param {function} onSearch - Callback when search is triggered (receives query string)
 * @param {boolean} loading - Loading state indicator
 * @param {number} debounceDelay - Debounce delay in milliseconds (default: 400ms)
 * @param {string} searchParamName - URL parameter name for search (default: 'search')
 * @param {string} pageParamName - URL parameter name for paging (default: 'page')
 */
const SearchInput = ({ 
  placeholder = "Search...", 
  onSearch,
  loading = false,
  debounceDelay = 400,
  searchParamName = 'search',
  pageParamName = 'page',
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchValue, setSearchValue] = useState('');
  const debounceTimerRef = useRef(null);
  const onSearchRef = useRef(onSearch);
  const initializedFromUrlRef = useRef(false);

  // Keep onSearch ref updated
  useEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);

  // Debounced search handler
  const debouncedSearch = useCallback((query) => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(() => {
      const trimmedQuery = query.trim();
      
      // Update URL params
      const newParams = new URLSearchParams(searchParams);
      
      if (trimmedQuery) {
        newParams.set(searchParamName, trimmedQuery);
        newParams.set(pageParamName, '1'); // Reset to first page on new search
      } else {
        newParams.delete(searchParamName);
        newParams.set(pageParamName, '1');
      }
      
      setSearchParams(newParams);
      
      // Call onSearch callback
      if (onSearchRef.current) {
        onSearchRef.current(trimmedQuery);
      }
    }, debounceDelay);
  }, [searchParams, setSearchParams, searchParamName, pageParamName, debounceDelay]);

  const handleChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    debouncedSearch(value);
  };

  const handleClear = () => {
    setSearchValue('');
    const newParams = new URLSearchParams(searchParams);
    newParams.delete(searchParamName);
    newParams.set(pageParamName, '1');
    setSearchParams(newParams);
    
    if (onSearchRef.current) {
      onSearchRef.current('');
    }
  };

  // Initialize from URL on first mount only
  useEffect(() => {
    if (initializedFromUrlRef.current) return;
    const initialSearch = searchParams.get(searchParamName) || '';
    setSearchValue(initialSearch);
    initializedFromUrlRef.current = true;
  }, [searchParamName, searchParams]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return (
    <div style={{ position: 'relative', width: '100%', maxWidth: '400px' }}>
      <Input
        type="text"
        value={searchValue}
        onChange={handleChange}
        placeholder={placeholder}
        prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
        suffix={
          <Space size="small">
            {loading && <LoadingOutlined spin style={{ color: '#1890ff' }} />}
            {searchValue && !loading && (
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={handleClear}
                size="small"
                style={{ 
                  padding: 0,
                  height: 'auto',
                  display: 'flex',
                  alignItems: 'center'
                }}
                aria-label="Clear search"
              />
            )}
          </Space>
        }
        // Don't disable input during loading - just show loading indicator
        // This ensures the search value persists across loads/reloads
        allowClear={false} // We handle clear manually
        style={{
          borderRadius: '6px',
          paddingRight: searchValue ? '60px' : '40px'
        }}
      />
    </div>
  );
};

export default SearchInput;

